import { useEffect } from 'react'
import { useRunOpenDSS } from '../../hooks/useEngineering.ts'
import { VDROP_COLORS } from '../../types/engineering.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import { friendlyError } from '../../utils/friendlyError.ts'

interface OpenDSSRunPanelProps {
  feederId: number
  /** Per-meter avg demand assumption (the dashboard slider). */
  avgDemandKw: number
}

function fmtPu(v: number | null): string {
  return v === null ? '—' : v.toFixed(4)
}

function fmtPower(v: number | null): string {
  return v === null ? '—' : v.toFixed(1)
}

function fmtPct(v: number | null): string {
  return v === null ? '—' : `${v.toFixed(0)}%`
}

function fmtAmps(v: number | null): string {
  return v === null ? '—' : v.toFixed(1)
}

export function OpenDSSRunPanel({ feederId, avgDemandKw }: OpenDSSRunPanelProps) {
  const { mutate, data, isPending, isError, error, reset } = useRunOpenDSS(feederId)

  // A stale solve must never render against an avg-demand assumption it did
  // not use (spec §3.5 / §9: mutations reset on input change).
  useEffect(() => {
    reset()
  }, [avgDemandKw, reset])

  function handleRun() {
    mutate({ avg_demand_kw: avgDemandKw })
  }

  const minUnder = data?.min_voltage_pu != null && data.min_voltage_pu < 0.95
  const maxOver = data?.max_voltage_pu != null && data.max_voltage_pu > 1.05

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Solve in OpenDSS</h3>
        <span className="text-xs text-gray-500">Live solve — not saved</span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleRun}
          disabled={isPending}
          className="text-sm px-4 py-1.5 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isPending && (
            <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          )}
          {isPending ? 'Solving...' : 'Solve in OpenDSS'}
        </button>
        <span className="text-xs text-gray-500">
          Runs the real OpenDSS engine on the exported feeder model.
        </span>
      </div>

      {isError && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          {friendlyError(error, 'solve in OpenDSS')}
        </div>
      )}

      {data && (
        <div className="space-y-3">
          <AsOfStamp computedAt={null} />
          {/* Convergence + topology */}
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium"
              style={{
                backgroundColor: (data.converged ? VDROP_COLORS.normal : VDROP_COLORS.violation) + '20',
                color: data.converged ? VDROP_COLORS.normal : VDROP_COLORS.violation,
              }}
            >
              {data.converged ? '✓ Converged' : '✕ Did not converge'}
            </span>
            <span className="text-xs text-gray-500">
              {data.n_buses} buses · {data.n_nodes} nodes
            </span>
          </div>

          {/* Voltage + power metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 rounded p-2 text-center">
              <div
                className="text-sm font-medium"
                style={{ color: minUnder ? VDROP_COLORS.violation : undefined }}
              >
                {fmtPu(data.min_voltage_pu)}
              </div>
              <div className="text-xs text-gray-500">Min Voltage p.u.</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2 text-center">
              <div
                className="text-sm font-medium"
                style={{ color: maxOver ? VDROP_COLORS.violation : undefined }}
              >
                {fmtPu(data.max_voltage_pu)}
              </div>
              <div className="text-xs text-gray-500">Max Voltage p.u.</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2 text-center">
              <div
                className="text-sm font-medium"
                style={{ color: data.undervoltage_count > 0 ? VDROP_COLORS.violation : undefined }}
              >
                {data.undervoltage_count} / {data.overvoltage_count}
              </div>
              <div className="text-xs text-gray-500">Under / Over Nodes</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2 text-center">
              <div className="text-sm font-medium text-gray-700">
                {fmtPower(data.total_power_kw)} / {fmtPower(data.total_power_kvar)}
              </div>
              <div className="text-xs text-gray-500">Source kW / kvar</div>
            </div>
          </div>

          {data.worst_undervoltage_bus && (
            <div className="text-xs" style={{ color: VDROP_COLORS.violation }}>
              Worst under-voltage bus: <span className="font-mono">{data.worst_undervoltage_bus}</span>
            </div>
          )}

          {/* Overloaded lines */}
          {data.overloaded_lines.length > 0 ? (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Overloaded Lines ({data.overloaded_line_count})
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                      <th className="py-1.5 pr-3 font-medium">Line</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Current (A)</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Norm (A)</th>
                      <th className="py-1.5 font-medium text-right">Loading</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.overloaded_lines.map(line => (
                      <tr key={line.name} className="border-b border-gray-100">
                        <td className="py-1.5 pr-3 font-mono text-gray-700">{line.name}</td>
                        <td className="py-1.5 pr-3 text-right text-gray-700">
                          {fmtAmps(line.current_amps)}
                        </td>
                        <td className="py-1.5 pr-3 text-right text-gray-500">
                          {fmtAmps(line.norm_amps)}
                        </td>
                        <td
                          className="py-1.5 text-right font-medium"
                          style={{
                            color:
                              line.loading_pct != null && line.loading_pct > 100
                                ? VDROP_COLORS.violation
                                : undefined,
                          }}
                        >
                          {fmtPct(line.loading_pct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            data.converged && (
              <div className="text-xs text-gray-500">No overloaded lines.</div>
            )
          )}

          {data.warnings.length > 0 && (
            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1 space-y-0.5">
              {data.warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
